import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiShield
} from 'react-icons/fi';
import ImmunizationService from '../../services/immunizationService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';

const ImmunizationTreatments = ({ onCountUpdate, componentName = 'ImmunizationTreatments' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [immunizations, setImmunizations] = useState([]);
  const [filteredImmunizations, setFilteredImmunizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedImmunization, setSelectedImmunization] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingImmunization, setEditingImmunization] = useState(null);
  
  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    vaccineName: '',
    manufacturer: '',
    facility: '',
    dateFrom: '',
    dateTo: '',
    status: 'All'
  });

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
  const loadImmunizations = useCallback(async () => {
    if (!patientId) {
      console.log('No patientId provided, component will show empty state');
      setImmunizations([]);
      onCountUpdate && onCountUpdate(componentName, 0);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Loading immunizations for patient:', patientId);
      const response = await ImmunizationService.getImmunizationsByPatient(patientId);
      console.log('Raw API response:', response);
      
      let immunizationsData = [];
      
      if (response.data) {
        immunizationsData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        immunizationsData = response;
      } else if (response.immunizations) {
        immunizationsData = Array.isArray(response.immunizations) ? response.immunizations : [response.immunizations];
      }

      const normalizedImmunizations = immunizationsData.map(immunization => {
        console.log('Processing immunization:', immunization);
        
        // Handle patient information (could be populated or just ID)
        const patientInfo = typeof immunization.patient === 'object' && immunization.patient 
          ? { id: immunization.patient._id, name: immunization.patient.name }
          : { id: immunization.patient || patientId, name: patientData.patientName || 'Unknown Patient' };

        // Handle addedBy information (could be populated or just ID)
        const addedByInfo = typeof immunization.addedby === 'object' && immunization.addedby
          ? { id: immunization.addedby._id, name: immunization.addedby.name, email: immunization.addedby.email }
          : { id: immunization.addedby, name: 'Unknown', email: '' };

        return {
          // Primary identifiers
          immunization_id: immunization._id || immunization.immunization_id || immunization.id,
          
          // Immunization information
          vaccine_name: immunization.vaccinename || '',
          manufacturer: immunization.manufacturer || '',
          dose_number: immunization.dosenumber || 0,
          date_administered: immunization.dateadministered || immunization.createdAt,
          administered_by: immunization.administeredby || '',
          facility: immunization.facility || '',
          batch_number: immunization.batchnumber || '',
          next_dose_due_date: immunization.nextdoseduedate || null,
          side_effects: immunization.sideeffects || '',
          comments: immunization.comments || '',
          
          // Dates
          created_date: immunization.createdAt || immunization.created_date,
          updated_date: immunization.updatedAt || immunization.updated_date,
          
          // Personnel
          added_by: addedByInfo.name,
          added_by_id: addedByInfo.id,
          added_by_email: addedByInfo.email,
          
          // Status
          status: immunization.status || '1',
          
          // Patient info
          patient_id: patientInfo.id,
          patient_name: patientInfo.name,
          
          // Preserve original for detailed view
          _original: immunization
        };
      });

      console.log('Normalized immunizations:', normalizedImmunizations);
      
      // **IMPORTANTE: Solo actualizar si el contador cambió**
      if (immunizations.length !== normalizedImmunizations.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${immunizations.length} a ${normalizedImmunizations.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedImmunizations.length);
      }
      
      setImmunizations(normalizedImmunizations);
    } catch (error) {
      console.error('Error loading immunizations:', error);
      setImmunizations([]);
      // También actualizar contador en caso de error
      if (immunizations.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, immunizations.length, onCountUpdate, componentName, patientData]);

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadImmunizationStats = useCallback(async () => {
    if (!patientId || immunizations.length === 0) return;
    
    try {
      const upcomingCount = immunizations.filter(immunization => {
        if (!immunization.next_dose_due_date) return false;
        const dueDate = new Date(immunization.next_dose_due_date);
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        return dueDate >= today && dueDate <= thirtyDaysFromNow;
      }).length;
      
      const activeCount = immunizations.filter(immunization => 
        immunization.status === '1'
      ).length;
      
      if (upcomingCount > 0 || activeCount > 0) {
        setAlertInfo({
          alert_type: 'immunization',
          message: `${activeCount} immunizations${upcomingCount > 0 ? `, ${upcomingCount} upcoming doses` : ''}`,
          priority: upcomingCount > 0 ? 'medium' : 'low',
          requires_action: upcomingCount > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading immunization stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, immunizations]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Immunizations array length:', immunizations.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...immunizations];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(immunization => 
        (immunization.vaccine_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (immunization.manufacturer || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (immunization.facility || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (immunization.administered_by || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Aplicar filtros específicos
    if (filters.status !== 'All') {
      if (filters.status === 'Active') {
        filtered = filtered.filter(immunization => immunization.status === '1');
      } else if (filters.status === 'Inactive') {
        filtered = filtered.filter(immunization => immunization.status === '0');
      }
    }

    if (filters.vaccineName) {
      filtered = filtered.filter(immunization => 
        (immunization.vaccine_name || '').toLowerCase().includes(filters.vaccineName.toLowerCase())
      );
    }

    if (filters.manufacturer) {
      filtered = filtered.filter(immunization => 
        (immunization.manufacturer || '').toLowerCase().includes(filters.manufacturer.toLowerCase())
      );
    }

    if (filters.facility) {
      filtered = filtered.filter(immunization => 
        (immunization.facility || '').toLowerCase().includes(filters.facility.toLowerCase())
      );
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(immunization => {
        const adminDate = immunization.date_administered;
        return adminDate && new Date(adminDate) >= new Date(filters.dateFrom);
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(immunization => {
        const adminDate = immunization.date_administered;
        return adminDate && new Date(adminDate) <= new Date(filters.dateTo);
      });
    }

    console.log('Filtered array length:', filtered.length);

    // Ordenamiento
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.date_administered || item.created_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'vaccine':
          const vaccineA = (a.vaccine_name || '').toLowerCase();
          const vaccineB = (b.vaccine_name || '').toLowerCase();
          return vaccineA.localeCompare(vaccineB);
        case 'dose':
          return (b.dose_number || 0) - (a.dose_number || 0);
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredImmunizations(filtered);
  }, [immunizations, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'vaccine': return 'By Vaccine';
      case 'dose': return 'By Dose';
      default: return 'Sort by';
    }
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'vaccine_name',
      header: 'Vaccine',
      size: 150,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.vaccine_name || 'Unknown Vaccine'}
            </div>
            <small className="text-muted">
              {formatDate(row.original.date_administered)}
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'manufacturer',
      header: 'Manufacturer',
      size: 120,
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row.original.manufacturer || 'Not specified'}
        </span>
      )
    },
    {
      accessorKey: 'dose_number',
      header: 'Dose',
      size: 80,
      Cell: ({ row }) => (
        <span className="badge bg-primary">
          Dose {row.original.dose_number || 1}
        </span>
      )
    },
    {
      accessorKey: 'administered_by',
      header: 'Administered By',
      size: 140,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.administered_by || 'Not specified'}</div>
          <small className="text-muted">
            {row.original.facility || 'Unknown facility'}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'next_dose_due_date',
      header: 'Next Dose',
      size: 120,
      Cell: ({ row }) => {
        const nextDate = row.original.next_dose_due_date;
        if (!nextDate) return <span className="text-muted">-</span>;
        
        const dueDate = new Date(nextDate);
        const today = new Date();
        const isOverdue = dueDate < today;
        const isUpcoming = dueDate <= new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        
        return (
          <span className={`badge bg-${isOverdue ? 'danger' : isUpcoming ? 'warning' : 'success'}`}>
            {formatDate(nextDate)}
          </span>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)}`}>
          {getStatusText(row.original.status)}
        </span>
      )
    },
    {
      accessorKey: 'added_by',
      header: 'Added By',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.added_by || 'Unknown'}</div>
          <small className="text-muted">
            {formatDate(row.original.created_date)}
          </small>
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewImmunization(row.original.immunization_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditImmunization(row.original.immunization_id);
            }}
            title="Edit"
          >
            <FiEdit3 />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteImmunization(row.original.immunization_id);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ], [immunizations]);

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadImmunizations();
    }
  }, [patientId, loadImmunizations]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (immunizations.length > 0) {
      loadImmunizationStats();
    }
  }, [immunizations, loadImmunizationStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${immunizations.length}`);
    onCountUpdate && onCountUpdate(componentName, immunizations.length);
  }, [immunizations.length, onCountUpdate, componentName]);

  // Apply filters whenever immunizations or filters change
  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- immunizations.length:', immunizations.length);
    console.log('- sortBy:', sortBy);
    
    if (immunizations.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay immunizations, limpiando filteredImmunizations');
      setFilteredImmunizations([]);
    }
  }, [immunizations, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showEditOffcanvas]);

  // Solo mostrar loading si estamos esperando datos específicos
  if (isLoading && patientId) {
    return <div>Loading patient data...</div>;
  }

  const handleViewImmunization = (immunizationId) => {
    console.log('Viewing immunization:', immunizationId);
    const immunization = immunizations.find(i => i.immunization_id === immunizationId);
    if (immunization) {
      console.log('Found immunization for viewing:', immunization);
      setSelectedImmunization(immunization);
      setShowViewOffcanvas(true);
    } else {
      console.error('Immunization not found:', immunizationId);
    }
  };
  
  const handleEditImmunization = (immunizationId) => {
    console.log('Editing immunization:', immunizationId);
    const immunization = immunizations.find(i => i.immunization_id === immunizationId);
    if (immunization) {
      console.log('Found immunization for editing:', immunization);
      setEditingImmunization(immunization);
      setShowEditOffcanvas(true);
    } else {
      console.error('Immunization not found for editing:', immunizationId);
    }
  };

  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowAddOffcanvas(true);
  };

  // **FUNCIÓN PARA MANEJAR LA CREACIÓN DE INMUNIZACIÓN**
  const handleSubmitNewImmunization = async (immunizationData) => {
    console.log('=== SUBMITTING NEW IMMUNIZATION ===');
    console.log('Original immunization data:', immunizationData);
    
    setIsSubmitting(true);
    
    try {
      const currentUser = getCurrentUser();
      const currentEcosystem = getCurrentEcosystem();
      
      console.log('Current user:', currentUser);
      console.log('Current ecosystem:', currentEcosystem);
      
      if (!currentUser || !currentUser.id) {
        throw new Error('No se pudo obtener información del usuario actual. Por favor, inicia sesión nuevamente.');
      }
      
      if (!currentEcosystem || !currentEcosystem.id) {
        throw new Error('No se pudo obtener información del ecosistema actual. Por favor, verifica tu configuración.');
      }

      if (!patientId) {
        throw new Error('No se pudo obtener el ID del paciente.');
      }

      const completeImmunizationData = {
        vaccinename: immunizationData.vaccinename || '',
        manufacturer: immunizationData.manufacturer || '',
        dosenumber: immunizationData.dosenumber || 1,
        dateadministered: immunizationData.dateadministered || new Date().toISOString().split('T')[0],
        administeredby: immunizationData.administeredby || '',
        facility: immunizationData.facility || '',
        batchnumber: immunizationData.batchnumber || '',
        nextdoseduedate: immunizationData.nextdoseduedate || null,
        sideeffects: immunizationData.sideeffects || '',
        comments: immunizationData.comments || '',
        
        patient: patientId,
        addedby: currentUser.id,
        ecosystemid: currentEcosystem.id,
        status: '1'
      };

      console.log('Complete immunization data to send:', completeImmunizationData);
      
      const result = await ImmunizationService.createImmunization(completeImmunizationData);
      console.log('Immunization created successfully:', result);
      
      await loadImmunizations();
      await loadImmunizationStats();
      
      setShowAddOffcanvas(false);
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Immunization created successfully!');
      } else {
        alert('Immunization created successfully!');
      }
      
    } catch (error) {
      console.error('Error creating immunization:', error);
      
      let errorMessage = 'Error creating immunization. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(`Error: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateImmunization = async (updatedData) => {
  console.log('=== UPDATING IMMUNIZATION ===');
  console.log('Updated immunization data:', updatedData);
  console.log('Editing immunization ID:', editingImmunization?.immunization_id);
  
  setIsSubmitting(true);
  
  try {
    if (!editingImmunization?.immunization_id) {
      throw new Error('No se pudo obtener el ID de la inmunización a actualizar');
    }

    // CAMBIO AQUÍ: Usar los nombres de campo que espera la API
    const updateData = {
      vaccinename: updatedData.vaccinename || '',
      manufacturer: updatedData.manufacturer || '',
      dosenumber: updatedData.dosenumber || 1,
      dateadministered: updatedData.dateadministered,
      administeredby: updatedData.administeredby || '',
      facility: updatedData.facility || '',
      batchnumber: updatedData.batchnumber || '',
      nextdoseduedate: updatedData.nextdoseduedate || null,
      sideeffects: updatedData.sideeffects || '',
      comments: updatedData.comments || ''
    };

    console.log('Update data to send:', updateData);
    
    const result = await ImmunizationService.updateImmunization(editingImmunization.immunization_id, updateData);
    console.log('Immunization updated successfully:', result);
    
    // Recargar datos después de la actualización
    await loadImmunizations();
    await loadImmunizationStats();
    
    // Cerrar el modal de edición
    setShowEditOffcanvas(false);
    setEditingImmunization(null);
    
    // Mostrar mensaje de éxito
    if (window.showSuccessMessage) {
      window.showSuccessMessage('Immunization updated successfully!');
    } else {
      alert('Immunization updated successfully!');
    }
    
  } catch (error) {
    console.error('Error updating immunization:', error);
    
    let errorMessage = 'Error updating immunization. Please try again.';
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }
    
    alert(`Error: ${errorMessage}`);
    throw error;
  } finally {
    setIsSubmitting(false);
  }
};

  // **FUNCIONES AUXILIARES PARA OBTENER DATOS DEL USUARIO**
  const getCurrentUser = () => {
    console.log('Getting current user...');
    
    try {
      const userData = localStorage.getItem('currentUser') || localStorage.getItem('user') || localStorage.getItem('authUser');
      if (userData) {
        const user = JSON.parse(userData);
        console.log('User found in localStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }

    try {
      const sessionUserData = sessionStorage.getItem('currentUser') || sessionStorage.getItem('user');
      if (sessionUserData) {
        const user = JSON.parse(sessionUserData);
        console.log('User found in sessionStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from sessionStorage:', error);
    }

    console.warn('No user data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439011',
      name: 'Test User',
      email: 'test@example.com'
    };
  };

  const getCurrentEcosystem = () => {
    console.log('Getting current ecosystem...');
    
    try {
      const ecosystemData = localStorage.getItem('currentEcosystem') || localStorage.getItem('ecosystem');
      if (ecosystemData) {
        const ecosystem = JSON.parse(ecosystemData);
        console.log('Ecosystem found in localStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from localStorage:', error);
    }

    try {
      const sessionEcosystemData = sessionStorage.getItem('currentEcosystem') || sessionStorage.getItem('ecosystem');
      if (sessionEcosystemData) {
        const ecosystem = JSON.parse(sessionEcosystemData);
        console.log('Ecosystem found in sessionStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from sessionStorage:', error);
    }

    console.warn('No ecosystem data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439012',
      name: 'Test Ecosystem'
    };
  };

  // 1. FUNCIÓN PARA ABRIR EL FILTRO OFFCANVAS
const handleOpenFilterOffcanvas = () => {
  console.log('Opening filter offcanvas');
  setShowFilterOffcanvas(true);
};

// 2. FUNCIÓN PARA OBTENER FILTROS ACTIVOS
const getActiveFilters = () => {
  const activeFilters = [];
  
  // Check each filter and add to active filters if it has a value
  if (filters.status && filters.status !== 'All') {
    activeFilters.push({
      key: 'status',
      label: `Status: ${filters.status}`,
      value: filters.status
    });
  }
  
  if (filters.vaccineName && filters.vaccineName.trim()) {
    activeFilters.push({
      key: 'vaccineName',
      label: `Vaccine: ${filters.vaccineName}`,
      value: filters.vaccineName
    });
  }
  
  if (filters.manufacturer && filters.manufacturer.trim()) {
    activeFilters.push({
      key: 'manufacturer',
      label: `Manufacturer: ${filters.manufacturer}`,
      value: filters.manufacturer
    });
  }
  
  if (filters.facility && filters.facility.trim()) {
    activeFilters.push({
      key: 'facility',
      label: `Facility: ${filters.facility}`,
      value: filters.facility
    });
  }
  
  if (filters.dateFrom) {
    activeFilters.push({
      key: 'dateFrom',
      label: `From: ${formatDate(filters.dateFrom)}`,
      value: filters.dateFrom
    });
  }
  
  if (filters.dateTo) {
    activeFilters.push({
      key: 'dateTo',
      label: `To: ${formatDate(filters.dateTo)}`,
      value: filters.dateTo
    });
  }
  
  // Add global filter if active
  if (globalFilter && globalFilter.trim()) {
    activeFilters.push({
      key: 'globalFilter',
      label: `Search: "${globalFilter}"`,
      value: globalFilter
    });
  }
  
  return activeFilters;
};

// 3. FUNCIÓN PARA REMOVER UN FILTRO ESPECÍFICO
const handleRemoveFilter = (filterToRemove) => {
  console.log('Removing filter:', filterToRemove);
  
  if (filterToRemove.key === 'globalFilter') {
    setGlobalFilter('');
  } else if (filterToRemove.key === 'status') {
    setFilters(prev => ({ ...prev, status: 'All' }));
  } else {
    setFilters(prev => ({ ...prev, [filterToRemove.key]: '' }));
  }
};

// 4. FUNCIÓN PARA LIMPIAR TODOS LOS FILTROS ACTIVOS
const handleClearAllActiveFilters = () => {
  console.log('Clearing all active filters');
  
  // Reset all filters to default values
  setFilters({
    vaccineName: '',
    manufacturer: '',
    facility: '',
    dateFrom: '',
    dateTo: '',
    status: 'All'
  });
  
  // Reset global filter
  setGlobalFilter('');
};


  const handleDeleteImmunization = async (immunizationId) => {
    if (window.confirm('Are you sure you want to delete this immunization?')) {
      try {
        await ImmunizationService.deleteImmunization(immunizationId);
        await loadImmunizations();
        await loadImmunizationStats();
      } catch (error) {
        console.error('Error deleting immunization:', error);
        alert('Error deleting immunization. Please try again.');
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      vaccineName: '',
      manufacturer: '',
      facility: '',
      dateFrom: '',
      dateTo: '',
      status: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedImmunization(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };

  const handleCloseEditOffcanvas = () => {
    console.log('Closing edit offcanvas');
    if (!isSubmitting) {
      setShowEditOffcanvas(false);
      setEditingImmunization(null);
    }
  };

  // **FUNCIONES AUXILIARES**
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '1':
        return 'success';
      case '0':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case '1':
        return 'Active';
      case '0':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  const getAlertIcon = () => {
    if (!alertInfo) return null;
    
    switch (alertInfo.priority) {
      case 'high':
        return <FiAlertTriangle className="text-danger" />;
      case 'medium':
        return <FiInfo className="text-warning" />;
      default:
        return <FiInfo className="text-info" />;
    }
  };

  const getAlertClass = () => {
    if (!alertInfo) return '';
    
    switch (alertInfo.priority) {
      case 'high':
        return 'alert-danger';
      case 'medium':
        return 'alert-warning';
      default:
        return 'alert-info';
    }
  };

  // **RENDER DEL COMPONENTE**
  return (
    <main id="app">
    <TopHeader />
    <div className="container-fluid w-100">
      <PatientManagement activeMenu='/my-immunizations'/>
      
      {/* Breadcrumb */}
      <div className="mb-3">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
            <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
            <li className="breadcrumb-item active" aria-current="page">
              My Immunizations {patientData?.patientName ? `- ${patientData.patientName}` : ''}
            </li>
          </ol>
        </nav>
      </div>

<div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

            {/* Right side - Controls */}
                            <div className="d-flex align-items-center gap-2">
                              {/* Sort By Dropdown */}
                              <div className="dropdown" style={{position: 'relative'}}>
                                <button
                                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSortDropdown(!showSortDropdown);
                                  }}
                                >
                                  {getSortDisplayText()}
                                  <FiChevronDown />
                                </button>
                                {showSortDropdown && (
                                  <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                                    <button
                                      className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                                      onClick={() => handleSortChange('newest')}
                                    >
                                      Newest First
                                    </button>
                                    <button
                                      className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                                      onClick={() => handleSortChange('oldest')}
                                    >
                                      Oldest First
                                    </button>
                                    <button
  className={`dropdown-item ${sortBy === 'vaccine' ? 'active' : ''}`}
  onClick={() => handleSortChange('vaccine')}
>
  By Vaccine
</button>
<button
  className={`dropdown-item ${sortBy === 'dose' ? 'active' : ''}`}
  onClick={() => handleSortChange('dose')}
>
  By Dose
</button>
                                  </div>
                                )}
                              </div>
            
                              {/* Filter Button */}
                              <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                onClick={handleOpenFilterOffcanvas}
                              >
                                <FiFilter />
                                Filter
                              </button>
                        
                              {/* Add New Button */}
                              <button
                                className="btn btn-primary d-flex align-items-center gap-2"
                                onClick={handleAddNew}
                              >
                                <FiPlus />
                                Add New
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
            
                      {/* Active Filters Display */}
                      {getActiveFilters().length > 0 && (
                        <div className="row mb-3">
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                              <span className="text-muted fw-medium">Active filters:</span>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                {getActiveFilters().map((filter) => (
                                  <span
                                    key={filter.key}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                    style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                                  >
                                    {filter.label}
                                    <button
                                      type="button"
                                      className="btn-close btn-close-white"
                                      style={{ 
                                        fontSize: '0.625rem',
                                        padding: '0.125rem',
                                        marginLeft: '0.25rem'
                                      }}
                                      onClick={() => handleRemoveFilter(filter)}
                                      title={`Remove ${filter.label} filter`}
                                    ></button>
                                  </span>
                                ))}
                                
                                {/* Clear All Button */}
                                {getActiveFilters().length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                    onClick={handleClearAllActiveFilters}
                                    title="Clear all filters"
                                  >
                                    <FiX size={14} />
                                    Clear All
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

            
            {/* Main Table */}
            <MaterialReactTable
              columns={columns}
              data={filteredImmunizations}

              
              // Configuración básica
              enableRowSelection
              enableColumnFilters
              enableGlobalFiltering
              enableSorting={false}
              enablePagination
              
              // Toolbars
              enableTopToolbar
              enableBottomToolbar
              
              // Features opcionales (puedes deshabilitarlas si no las necesitas)
              enableDensityToggle
              enableFullScreenToggle
              enableHiding
              enableColumnOrdering
              enableColumnResizing={false}  // Deshabilitar si causa problemas
              enableGrouping={false}        // Deshabilitar si causa problemas
              
              // Estados
              initialState={{
                pagination: { pageSize: 10, pageIndex: 0 },
                sorting: [{ id: 'vaccine_name', desc: false }], // Cambiar a columna existente
                density: 'comfortable',
                showGlobalFilter: false
              }}
              
              state={{
                isLoading,
                rowSelection,
                globalFilter
              }}
              
              // Callbacks
              onRowSelectionChange={setRowSelection}
              onGlobalFilterChange={handleGlobalFilterChange}
              
              // Configuración de renderizado
              muiTableContainerProps={{
                sx: { maxHeight: '600px' }
              }}
                      />
            
                      {/* Results Summary */}
                      {filteredImmunizations.length > 0 && (
                        <div className="mt-3">
                          <small className="text-muted">
Showing {filteredImmunizations.length} of {immunizations.length} immunizations
                            {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050 }}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Filter & Search</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowFilterOffcanvas(false)}
            />
          </div>
          <div className="offcanvas-body">
            {/* Global Search */}
            <div className="mb-3">
              <label className="form-label">Search</label>
              <div className="input-group">
                <span className="input-group-text">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search vaccines, manufacturers, facilities..."
                  value={globalFilter}
                  onChange={(e) => handleGlobalFilterChange(e.target.value)}
                />
              </div>
            </div>

            <hr />

            {/* Specific Filters */}
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="All">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Vaccine Name</label>
              <input
                type="text"
                className="form-control"
                value={filters.vaccineName}
                onChange={(e) => handleFilterChange('vaccineName', e.target.value)}
                placeholder="Filter by vaccine name"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Manufacturer</label>
              <input
                type="text"
                className="form-control"
                value={filters.manufacturer}
                onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
                placeholder="Filter by manufacturer"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Facility</label>
              <input
                type="text"
                className="form-control"
                value={filters.facility}
                onChange={(e) => handleFilterChange('facility', e.target.value)}
                placeholder="Filter by facility"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Date From</label>
              <input
                type="date"
                className="form-control"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Date To</label>
              <input
                type="date"
                className="form-control"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-primary flex-grow-1"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Offcanvas */}
      {showViewOffcanvas && selectedImmunization && (
        <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '500px' }}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Immunization Details</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleCloseViewOffcanvas}
            />
          </div>
          <div className="offcanvas-body">
            <div className="row">
              <div className="col-12">
                <div className="card border-0 bg-light">
                  <div className="card-body">
                    <h6 className="card-title text-primary">
                      <FiShield className="me-2" />
                      {selectedImmunization.vaccine_name}
                    </h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <small className="text-muted">Dose Number</small>
                        <div className="fw-bold">{selectedImmunization.dose_number || 1}</div>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Status</small>
                        <div>
                          <span className={`badge bg-${getStatusColor(selectedImmunization.status)}`}>
                            {getStatusText(selectedImmunization.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <h6>Basic Information</h6>
              <div className="row g-2">
                <div className="col-12">
                  <small className="text-muted">Manufacturer</small>
                  <div>{selectedImmunization.manufacturer || 'Not specified'}</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Date Administered</small>
                  <div>{formatDate(selectedImmunization.date_administered)}</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Batch Number</small>
                  <div>{selectedImmunization.batch_number || 'Not specified'}</div>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <h6>Administration Details</h6>
              <div className="row g-2">
                <div className="col-12">
                  <small className="text-muted">Administered By</small>
                  <div>{selectedImmunization.administered_by || 'Not specified'}</div>
                </div>
                <div className="col-12">
                  <small className="text-muted">Facility</small>
                  <div>{selectedImmunization.facility || 'Not specified'}</div>
                </div>
                <div className="col-12">
                  <small className="text-muted">Next Dose Due Date</small>
                  <div>{selectedImmunization.next_dose_due_date ? formatDate(selectedImmunization.next_dose_due_date) : 'Not scheduled'}</div>
                </div>
              </div>
            </div>

            {selectedImmunization.side_effects && (
              <div className="mt-3">
                <h6>Side Effects</h6>
                <div className="bg-light p-2 rounded">
                  {selectedImmunization.side_effects}
                </div>
              </div>
            )}

            {selectedImmunization.comments && (
              <div className="mt-3">
                <h6>Comments</h6>
                <div className="bg-light p-2 rounded">
                  {selectedImmunization.comments}
                </div>
              </div>
            )}

            <div className="mt-3">
              <h6>Record Information</h6>
              <div className="row g-2">
                <div className="col-12">
                  <small className="text-muted">Added By</small>
                  <div>{selectedImmunization.added_by || 'Unknown'}</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Created Date</small>
                  <div>{formatDate(selectedImmunization.created_date)}</div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Updated Date</small>
                  <div>{formatDate(selectedImmunization.updated_date)}</div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-primary flex-grow-1"
                  onClick={() => {
                    handleCloseViewOffcanvas();
                    handleEditImmunization(selectedImmunization.immunization_id);
                  }}
                >
                  <FiEdit3 className="me-1" /> Edit
                </button>
                <button
                  className="btn btn-outline-danger"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this immunization?')) {
                      handleDeleteImmunization(selectedImmunization.immunization_id);
                      handleCloseViewOffcanvas();
                    }
                  }}
                >
                  <FiTrash2 />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Immunization Offcanvas */}
      {showAddOffcanvas && (
        <AddImmunizationForm
          isOpen={showAddOffcanvas}
          onClose={handleCloseAddOffcanvas}
          onSubmit={handleSubmitNewImmunization}
          isSubmitting={isSubmitting}
          patientData={patientData}
        />
      )}

      {/* Edit Immunization Offcanvas */}
      {showEditOffcanvas && editingImmunization && (
        <EditImmunizationForm
          isOpen={showEditOffcanvas}
          onClose={handleCloseEditOffcanvas}
          onSubmit={handleUpdateImmunization}
          immunizationData={editingImmunization}
          isSubmitting={isSubmitting}
          patientData={patientData}
        />
      )}

      {/* Backdrop for offcanvas */}
      {(showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) && (
        <div 
          className="offcanvas-backdrop fade show" 
          onClick={() => {
            if (!isSubmitting) {
              setShowFilterOffcanvas(false);
              setShowAddOffcanvas(false);
              setShowViewOffcanvas(false);
              setShowEditOffcanvas(false);
            }
          }}
        />
      )}
     {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

// AddImmunizationForm Component
const AddImmunizationForm = ({ isOpen, onClose, onSubmit, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    vaccinename: '',
    manufacturer: '',
    dosenumber: 1,
    dateadministered: new Date().toISOString().split('T')[0],
    administeredby: '',
    facility: '',
    batchnumber: '',
    nextdoseduedate: '',
    sideeffects: '',
    comments: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vaccinename.trim()) {
      newErrors.vaccinename = 'Vaccine name is required';
    }

    if (!formData.dateadministered) {
      newErrors.dateadministered = 'Administration date is required';
    }

    if (!formData.dosenumber || formData.dosenumber < 1) {
      newErrors.dosenumber = 'Dose number must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        vaccinename: '',
        manufacturer: '',
        dosenumber: 1,
        dateadministered: new Date().toISOString().split('T')[0],
        administeredby: '',
        facility: '',
        batchnumber: '',
        nextdoseduedate: '',
        sideeffects: '',
        comments: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '500px' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">Add New Immunization</h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          disabled={isSubmitting}
        />
      </div>
      <div className="offcanvas-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Patient Info */}
            <div className="col-12">
              <div className="alert alert-info">
                <FiUser className="me-2" />
                Adding immunization for: <strong>{patientData?.patientName}</strong>
              </div>
            </div>

            {/* Vaccine Name */}
            <div className="col-12">
              <label className="form-label required">Vaccine Name *</label>
              <input
                type="text"
                className={`form-control ${errors.vaccinename ? 'is-invalid' : ''}`}
                value={formData.vaccinename}
                onChange={(e) => handleInputChange('vaccinename', e.target.value)}
                placeholder="e.g., COVID-19, Influenza, Hepatitis B"
                disabled={isSubmitting}
              />
              {errors.vaccinename && <div className="invalid-feedback">{errors.vaccinename}</div>}
            </div>

            {/* Manufacturer */}
            <div className="col-md-6">
              <label className="form-label">Manufacturer</label>
              <input
                type="text"
                className="form-control"
                value={formData.manufacturer}
                onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                placeholder="e.g., Pfizer, Moderna"
                disabled={isSubmitting}
              />
            </div>

            {/* Dose Number */}
            <div className="col-md-6">
              <label className="form-label required">Dose Number *</label>
              <input
                type="number"
                min="1"
                className={`form-control ${errors.dosenumber ? 'is-invalid' : ''}`}
                value={formData.dosenumber}
                onChange={(e) => handleInputChange('dosenumber', parseInt(e.target.value) || 1)}
                disabled={isSubmitting}
              />
              {errors.dosenumber && <div className="invalid-feedback">{errors.dosenumber}</div>}
            </div>

            {/* Date Administered */}
            <div className="col-md-6">
              <label className="form-label required">Date Administered *</label>
              <input
                type="date"
                className={`form-control ${errors.dateadministered ? 'is-invalid' : ''}`}
                value={formData.dateadministered}
                onChange={(e) => handleInputChange('dateadministered', e.target.value)}
                disabled={isSubmitting}
              />
              {errors.dateadministered && <div className="invalid-feedback">{errors.dateadministered}</div>}
            </div>

            {/* Next Dose Due Date */}
            <div className="col-md-6">
              <label className="form-label">Next Dose Due Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.nextdoseduedate}
                onChange={(e) => handleInputChange('nextdoseduedate', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {/* Administered By */}
            <div className="col-12">
              <label className="form-label">Administered By</label>
              <input
                type="text"
                className="form-control"
                value={formData.administeredby}
                onChange={(e) => handleInputChange('administeredby', e.target.value)}
                placeholder="Healthcare provider name"
                disabled={isSubmitting}
              />
            </div>

            {/* Facility */}
            <div className="col-md-8">
              <label className="form-label">Facility</label>
              <input
                type="text"
                className="form-control"
                value={formData.facility}
                onChange={(e) => handleInputChange('facility', e.target.value)}
                placeholder="Hospital, clinic, or facility name"
                disabled={isSubmitting}
              />
            </div>

            {/* Batch Number */}
            <div className="col-md-4">
              <label className="form-label">Batch Number</label>
              <input
                type="text"
                className="form-control"
                value={formData.batchnumber}
                onChange={(e) => handleInputChange('batchnumber', e.target.value)}
                placeholder="Batch/Lot #"
                disabled={isSubmitting}
              />
            </div>

            {/* Side Effects */}
            <div className="col-12">
              <label className="form-label">Side Effects</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.sideeffects}
                onChange={(e) => handleInputChange('sideeffects', e.target.value)}
                placeholder="Any observed side effects or reactions"
                disabled={isSubmitting}
              />
            </div>

            {/* Comments */}
            <div className="col-12">
              <label className="form-label">Additional Comments</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.comments}
                onChange={(e) => handleInputChange('comments', e.target.value)}
                placeholder="Any additional notes or comments"
                disabled={isSubmitting}
              />
            </div>

            {/* Action Buttons */}
            <div className="col-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FiCheck className="me-2" />
                      Create Immunization
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// EditImmunizationForm Component - Fixed version
const EditImmunizationForm = ({ isOpen, onClose, onSubmit, immunizationData, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Function to safely get nested property values
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue;
      }, obj);
    } catch (error) {
      return defaultValue;
    }
  };

  // Function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Initialize form data when immunizationData changes
  useEffect(() => {
    if (immunizationData) {
      console.log('Setting up edit form with data:', immunizationData);
      
      setFormData({
        vaccinename: safeGet(immunizationData, 'vaccine_name') || safeGet(immunizationData, 'vaccinename'),
        manufacturer: safeGet(immunizationData, 'manufacturer'),
        dosenumber: safeGet(immunizationData, 'dose_number') || safeGet(immunizationData, 'dosenumber') || 1,
        dateadministered: formatDateForInput(
          safeGet(immunizationData, 'date_administered') || 
          safeGet(immunizationData, 'dateadministered')
        ),
        administeredby: safeGet(immunizationData, 'administered_by') || safeGet(immunizationData, 'administeredby'),
        facility: safeGet(immunizationData, 'facility'),
        batchnumber: safeGet(immunizationData, 'batch_number') || safeGet(immunizationData, 'batchnumber'),
        nextdoseduedate: formatDateForInput(
          safeGet(immunizationData, 'next_dose_due_date') || 
          safeGet(immunizationData, 'nextdoseduedate')
        ),
        sideeffects: safeGet(immunizationData, 'side_effects') || safeGet(immunizationData, 'sideeffects'),
        comments: safeGet(immunizationData, 'comments')
      });
      
      // Clear any previous errors
      setErrors({});
    }
  }, [immunizationData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vaccinename || !formData.vaccinename.trim()) {
      newErrors.vaccinename = 'Vaccine name is required';
    }

    if (!formData.dateadministered) {
      newErrors.dateadministered = 'Administration date is required';
    }

    if (!formData.dosenumber || formData.dosenumber < 1) {
      newErrors.dosenumber = 'Dose number must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Clean up the data before submitting
      const cleanFormData = {
        ...formData,
        dosenumber: parseInt(formData.dosenumber) || 1,
        vaccinename: formData.vaccinename.trim(),
        manufacturer: formData.manufacturer?.trim() || '',
        administeredby: formData.administeredby?.trim() || '',
        facility: formData.facility?.trim() || '',
        batchnumber: formData.batchnumber?.trim() || '',
        sideeffects: formData.sideeffects?.trim() || '',
        comments: formData.comments?.trim() || ''
      };

      console.log('Submitting edit form with data:', cleanFormData);
      await onSubmit(cleanFormData);
    } catch (error) {
      console.error('Error submitting edit form:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '500px' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">Edit Immunization</h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          disabled={isSubmitting}
        />
      </div>
      <div className="offcanvas-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Patient Info */}
            <div className="col-12">
              <div className="alert alert-info">
                <FiUser className="me-2" />
                Editing immunization for: <strong>{patientData?.patientName}</strong>
              </div>
            </div>

            {/* Vaccine Name */}
            <div className="col-12">
              <label className="form-label required">Vaccine Name *</label>
              <input
                type="text"
                className={`form-control ${errors.vaccinename ? 'is-invalid' : ''}`}
                value={formData.vaccinename || ''}
                onChange={(e) => handleInputChange('vaccinename', e.target.value)}
                placeholder="e.g., COVID-19, Influenza, Hepatitis B"
                disabled={isSubmitting}
              />
              {errors.vaccinename && <div className="invalid-feedback">{errors.vaccinename}</div>}
            </div>

            {/* Manufacturer */}
            <div className="col-md-6">
              <label className="form-label">Manufacturer</label>
              <input
                type="text"
                className="form-control"
                value={formData.manufacturer || ''}
                onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                placeholder="e.g., Pfizer, Moderna"
                disabled={isSubmitting}
              />
            </div>

            {/* Dose Number */}
            <div className="col-md-6">
              <label className="form-label required">Dose Number *</label>
              <input
                type="number"
                min="1"
                className={`form-control ${errors.dosenumber ? 'is-invalid' : ''}`}
                value={formData.dosenumber || 1}
                onChange={(e) => handleInputChange('dosenumber', parseInt(e.target.value) || 1)}
                disabled={isSubmitting}
              />
              {errors.dosenumber && <div className="invalid-feedback">{errors.dosenumber}</div>}
            </div>

            {/* Date Administered */}
            <div className="col-md-6">
              <label className="form-label required">Date Administered *</label>
              <input
                type="date"
                className={`form-control ${errors.dateadministered ? 'is-invalid' : ''}`}
                value={formData.dateadministered || ''}
                onChange={(e) => handleInputChange('dateadministered', e.target.value)}
                disabled={isSubmitting}
              />
              {errors.dateadministered && <div className="invalid-feedback">{errors.dateadministered}</div>}
            </div>

            {/* Next Dose Due Date */}
            <div className="col-md-6">
              <label className="form-label">Next Dose Due Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.nextdoseduedate || ''}
                onChange={(e) => handleInputChange('nextdoseduedate', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {/* Administered By */}
            <div className="col-12">
              <label className="form-label">Administered By</label>
              <input
                type="text"
                className="form-control"
                value={formData.administeredby || ''}
                onChange={(e) => handleInputChange('administeredby', e.target.value)}
                placeholder="Healthcare provider name"
                disabled={isSubmitting}
              />
            </div>

            {/* Facility */}
            <div className="col-md-8">
              <label className="form-label">Facility</label>
              <input
                type="text"
                className="form-control"
                value={formData.facility || ''}
                onChange={(e) => handleInputChange('facility', e.target.value)}
                placeholder="Hospital, clinic, or facility name"
                disabled={isSubmitting}
              />
            </div>

            {/* Batch Number */}
            <div className="col-md-4">
              <label className="form-label">Batch Number</label>
              <input
                type="text"
                className="form-control"
                value={formData.batchnumber || ''}
                onChange={(e) => handleInputChange('batchnumber', e.target.value)}
                placeholder="Batch/Lot #"
                disabled={isSubmitting}
              />
            </div>

            {/* Side Effects */}
            <div className="col-12">
              <label className="form-label">Side Effects</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.sideeffects || ''}
                onChange={(e) => handleInputChange('sideeffects', e.target.value)}
                placeholder="Any observed side effects or reactions"
                disabled={isSubmitting}
              />
            </div>

            {/* Comments */}
            <div className="col-12">
              <label className="form-label">Additional Comments</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.comments || ''}
                onChange={(e) => handleInputChange('comments', e.target.value)}
                placeholder="Any additional notes or comments"
                disabled={isSubmitting}
              />
            </div>

            {/* Action Buttons */}
            <div className="col-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiCheck className="me-2" />
                      Update Immunization
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ImmunizationTreatments;